import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, User, Search, Plus, ChevronDown } from 'lucide-react';
import { apiService } from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from './ui/input-group';

interface HeaderProps {
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  onCreateNew?: () => void;
  showSearch?: boolean;
  usernameFilters?: string[];
  onUsernameFiltersChange?: (filters: string[]) => void;
}

export const Header: React.FC<HeaderProps> = ({
  searchTerm = '',
  onSearchChange,
  onCreateNew,
  showSearch = false,
  usernameFilters = [],
  onUsernameFiltersChange
}) => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Show search on home page
  const shouldShowSearch = showSearch || location.pathname === '/';

  return (
    <>
      <header className="flex px-2 border-b border-gray-200 bg-white sticky top-0 z-50 w-full">
        <div className="flex w-full px-4 py-2">
          <div className="flex flex-row w-full items-center gap-4">
            {/* Left section - Logo (1/4 width) */}
            <div className="flex items-center w-1/4">
              <Link
                to="/"
                className="text-xl font-extrabold text-gray-900 hover:text-gray-700 transition-colors"
              >
                OnePagers
              </Link>
            </div>

            {/* Center section - (empty, search will be outside header) */}
            <div className="flex justify-center w-1/2"></div>

            {/* Right section - Actions and User (1/4 width) */}
            <nav className="flex items-center justify-end gap-4 w-1/4">
              {shouldShowSearch && onCreateNew && (
                <Button
                  onClick={onCreateNew}
                  size="sm"
                  className="bg-gray-900 hover:bg-gray-800 text-white h-8 px-3 font-normal text-xs rounded-sm"
                >
                  <Plus className="w-4 h-4" /> New
                </Button>
              )}

              {isAuthenticated ? (
                <div className="relative">
                  <Button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    variant="ghost"
                    className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline font-medium">{user?.username || user?.fullName || user?.email}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>

                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                      <div className="p-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.username || user?.fullName}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <Button
                        onClick={() => {
                          navigate('/profile');
                          setShowUserMenu(false);
                        }}
                        variant="ghost"
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <User className="w-4 h-4" />
                        My Profile
                      </Button>
                      <Button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        variant="ghost"
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900 h-8 px-3 font-normal"
                  >
                    <Link to="/login">Sign in</Link>
                  </Button>
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>
      {/* Search bar and username filter outside header */}
      {shouldShowSearch && onSearchChange && (
        <div className="flex z-50 flex-col gap-2 pt-8 px-6 max-w-6xl mx-auto md:max-w-4xl">
          <div className="flex flex-col md:flex-row gap-2 w-full md:gap-4">
            <div className="w-full md:w-2/3">
              <div className="flex items-center border border-gray-200 rounded px-2 h-9 bg-white">
                <Search className="text-gray-400 w-4 h-4 mr-2" />
                <Input
                  type="text"
                  placeholder="Search and build apps from one pagers..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="placeholder-gray-400 border-0 focus:ring-0 h-8"
                  autoFocus={false}
                />
              </div>
            </div>
            <div className="w-full md:w-1/3 md:mt-0">
              <UsernameDropdown
                selected={usernameFilters}
                onChange={onUsernameFiltersChange}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// UsernameDropdown: Dropdown with all users, search bar to filter usernames
const UsernameDropdown: React.FC<{
  selected: string[];
  onChange?: (usernames: string[]) => void;
}> = ({ selected, onChange }) => {
  const [allUsers, setAllUsers] = useState<{ username: string }[]>([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await apiService.supabase.from('users').select('username');
      if (!error && data) setAllUsers(data.filter(u => u.username));
    })();
  }, []);

  // Debounced search
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = allUsers.filter(u => u.username.toLowerCase().includes(debounced.toLowerCase()));

  const toggle = (uname: string) => {
    if (!onChange) return;
    if (selected.includes(uname)) {
      onChange(selected.filter(u => u !== uname));
    } else {
      onChange([...selected, uname]);
    }
    setOpen(false); // Close dropdown after selection
  };

  // For portal positioning
  const [dropdownPos, setDropdownPos] = useState<{top: number, left: number, width: number} | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [open]);

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        type="button"
        variant="outline"
        className="w-full border border-gray-200 rounded px-2 h-9 bg-white text-left text-sm flex items-center justify-between"
        onClick={() => setOpen(o => !o)}
      >
        {selected.length > 0 ? selected.join(', ') : 'All users'}
        <ChevronDown className="w-4 h-4 ml-2" />
      </Button>
      {open && dropdownPos && createPortal(
        <div
          className="z-[9999] fixed bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width
          }}
        >
          <div className="p-2">
            <Input
              type="text"
              className="w-full border border-gray-200 rounded px-2 h-9 text-sm mb-2"
              placeholder="Search usernames..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
            {filtered.length === 0 && <div className="text-xs text-gray-400 p-2">No users found</div>}
            {filtered.map(u => (
              <div
                key={u.username}
                className={`cursor-pointer px-2 py-1 rounded text-sm ${selected.includes(u.username) ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                onClick={() => toggle(u.username)}
              >
                {u.username}
                {selected.includes(u.username) && <span className="ml-2 text-xs">✓</span>}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};